import { useState, useCallback, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useDialog } from '../../shared/hooks/useDialog';
import {
  cancelChallenge,
  declineChallenge,
  acceptChallenge,
  updatePredictionsBatch,
} from '../helpers/challengeCardApi';
import {
  transformPredictionsToApiFormat,
  hasPredictions,
  getPredictionCount,
  buildPredictionsSuccessMessage,
} from '../helpers/challengeActions.helper';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useInvalidateXpSummary } from '../../economy/hooks/useXpSummary';
import { hasEnoughCoins } from '../../shared/helpers/coins.helper';
import { useEconomyStore } from '../../shared/stores/economyStore';
import type { ChallengeWithStats } from '../types/challengeCard.types';
import type { UserPredictions } from '../types/challengeCard.types';
import type { SnackbarVariant } from '../../shared/stores/snackbarStore';

/** Remove a challenge from all active-challenges caches without refetching. */
function removeChallengeFromCache(queryClient: QueryClient, challengeId: string) {
  queryClient.setQueriesData<ChallengeWithStats[]>(
    { queryKey: ['challenges', 'active'] },
    (old) => old?.filter((c) => c.id !== challengeId)
  );
}

interface UseChallengeActionsProps {
  challenge: ChallengeWithStats;
  currentUserId: string;
  userPredictions: UserPredictions;
  isPendingParticipant: boolean;
  onShowMessage?: (payload: { message: string; variant: SnackbarVariant }) => void;
  onChallengeCancelled?: () => void;
}

interface UseChallengeActionsReturn {
  isUpdating: boolean;
  handleCancelChallenge: () => Promise<void>;
  handleDeclineChallenge: () => Promise<void>;
  handleUpdatePredictions: () => Promise<void>;
}

export const useChallengeActions = ({
  challenge,
  currentUserId,
  userPredictions,
  isPendingParticipant,
  onShowMessage,
  onChallengeCancelled,
}: UseChallengeActionsProps): UseChallengeActionsReturn => {
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();
  const { showDialog } = useDialog();
  const apiClient = useApiClient();
  const invalidateXp = useInvalidateXpSummary();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;
  const userCoins = useEconomyStore((state) => state.coins);

  const handleCancelChallenge = useCallback(async () => {
    // Show confirmation dialog
    const confirmMessage = 'Are you sure you want to cancel this challenge?';
    const confirmed = await showDialog({
      title: 'Cancel Challenge',
      message: confirmMessage,
      type: 'danger',
      confirmText: 'Yes, Cancel',
      cancelText: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    // User confirmed, proceed with cancellation
    try {
      const result = await cancelChallenge(challenge.id, apiClientRef.current);

      if (result.success) {
        if ((challenge.stakeCoins ?? 0) > 0) {
          useEconomyStore.getState().syncCoins(useEconomyStore.getState().coins + (challenge.stakeCoins ?? 0));
        }
        removeChallengeFromCache(queryClient, challenge.id);
        if (challenge.groupId) {
          queryClient.invalidateQueries({ queryKey: ['groups', challenge.groupId, 'challenges'] });
          queryClient.invalidateQueries({ queryKey: ['groups', challenge.groupId, 'challenges', 'joinable'] });
          queryClient.invalidateQueries({ queryKey: ['groups', 'dashboard', challenge.groupId] });
        }
        onShowMessage?.({
          message: 'Challenge cancelled successfully',
          variant: 'success',
        });
        onChallengeCancelled?.();
      } else {
        onShowMessage?.({
          message: result.error || 'Failed to cancel challenge',
          variant: 'error',
        });
      }
    } catch {
      onShowMessage?.({
        message: 'Failed to cancel challenge. Please try again.',
        variant: 'error',
      });
    }
  }, [challenge.id, challenge.stakeCoins, onShowMessage, onChallengeCancelled, showDialog, queryClient]);

  const handleDeclineChallenge = useCallback(async () => {
    try {
      const result = await declineChallenge(challenge.id, apiClientRef.current);

      if (result.success) {
        removeChallengeFromCache(queryClient, challenge.id);
        if (challenge.groupId) {
          queryClient.invalidateQueries({ queryKey: ['groups', challenge.groupId, 'challenges'] });
          queryClient.invalidateQueries({ queryKey: ['groups', challenge.groupId, 'challenges', 'joinable'] });
          queryClient.invalidateQueries({ queryKey: ['groups', 'dashboard', challenge.groupId] });
        }
        onShowMessage?.({
          message: 'Challenge invitation declined',
          variant: 'info',
        });
        onChallengeCancelled?.();
      } else {
        onShowMessage?.({
          message: result.error || 'Failed to decline challenge',
          variant: 'error',
        });
      }
    } catch {
      onShowMessage?.({
        message: 'Failed to decline challenge. Please try again.',
        variant: 'error',
      });
    }
  }, [challenge.id, onShowMessage, onChallengeCancelled, queryClient]);

  const handleUpdatePredictions = useCallback(async () => {
    setIsUpdating(true);

    try {
      if (isPendingParticipant && !hasEnoughCoins(userCoins, challenge.stakeCoins)) {
        onShowMessage?.({
          message: `You need ${challenge.stakeCoins ?? 0} coins to accept this challenge.`,
          variant: 'error',
        });
        return;
      }

      // If user is a pending participant, accept the challenge first
      if (isPendingParticipant) {
        const acceptResult = await acceptChallenge(challenge.id, apiClientRef.current);

        if (!acceptResult.success) {
          throw new Error(acceptResult.error || 'Failed to accept challenge');
        }
        // Accepting awards XP server-side; refetch so the header/"+X XP" flash surfaces now.
        invalidateXp();
        if ((challenge.stakeCoins ?? 0) > 0) {
          useEconomyStore.getState().syncCoins(useEconomyStore.getState().coins - (challenge.stakeCoins ?? 0));
        }
      }

      // Transform predictions using helper
      const predictionsToUpdate = transformPredictionsToApiFormat(challenge.id, currentUserId, userPredictions);

      if (!hasPredictions(userPredictions)) {
        // If accepting without predictions, just show accept message
        if (isPendingParticipant) {
          onShowMessage?.({
            message: 'Challenge accepted successfully',
            variant: 'success',
          });
          onChallengeCancelled?.();
        } else {
          onShowMessage?.({
            message: 'No predictions to update!',
            variant: 'info',
          });
        }
        setIsUpdating(false);
        return;
      }

      // Send batch update to API
      const result = await updatePredictionsBatch(predictionsToUpdate, apiClientRef.current);

      if (!result.success) {
        throw new Error(result.error || 'Failed to update predictions');
      }

      // Background-refresh so the list picks up the updated status/predictions without blocking the UI.
      queryClient.invalidateQueries({ queryKey: ['challenges', 'active'] });
      if (challenge.groupId) {
        queryClient.invalidateQueries({ queryKey: ['groups', challenge.groupId, 'challenges'] });
        queryClient.invalidateQueries({ queryKey: ['groups', challenge.groupId, 'challenges', 'joinable'] });
        queryClient.invalidateQueries({ queryKey: ['groups', 'dashboard', challenge.groupId] });
      }

      const predictionCount = getPredictionCount(userPredictions);
      const message = buildPredictionsSuccessMessage(predictionCount, isPendingParticipant);

      onShowMessage?.({
        message,
        variant: 'success',
      });

      if (isPendingParticipant) {
        onChallengeCancelled?.();
      }
    } catch {
      onShowMessage?.({
        message: isPendingParticipant
          ? 'Failed to accept challenge. Please try again.'
          : 'Failed to update predictions. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsUpdating(false);
    }
  }, [challenge.id, challenge.stakeCoins, currentUserId, userPredictions, isPendingParticipant, onShowMessage, onChallengeCancelled, queryClient, userCoins, invalidateXp]);

  return {
    isUpdating,
    handleCancelChallenge,
    handleDeclineChallenge,
    handleUpdatePredictions,
  };
};
